import React, { useState, useEffect } from 'react';
import CMSCustomSections from '../components/CMSCustomSections';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { User, Car, ArrowRight, Star, Shield, Thermometer, Target, Feather, Quote, Mail, Play, ChevronRight } from 'lucide-react';
import { useCMS, getImageUrl } from '../hooks/useCMS';
import { toast } from '../hooks/use-toast';
import QuickQuote from '../components/QuickQuote';

const API = process.env.REACT_APP_BACKEND_URL;

const LandingHome = () => {
  const navigate = useNavigate();
  const { content } = useCMS('home');
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [featuredVideo, setFeaturedVideo] = useState(null);
  const [fleetHeroImage, setFleetHeroImage] = useState(null);

  // Fetch featured video and fleet hero image
  useEffect(() => {
    fetch(`${API}/api/fleet-media/site-featured`)
      .then(r => r.ok ? r.json() : {})
      .then(data => { if (data.url) setFeaturedVideo(data.url); })
      .catch(() => {});
    // Use a real fleet photo as hero background
    fetch(`${API}/api/fleet-media/all`)
      .then(r => r.ok ? r.json() : { vehicles: [] })
      .then(data => {
        const allImages = [];
        (data.vehicles || []).forEach(v => (v.images || []).forEach(img => allImages.push(img.url)));
        if (allImages.length > 0) setFleetHeroImage(`${API}${allImages[0]}`);
      })
      .catch(() => {});
  }, []);

  const heroImage = fleetHeroImage || getImageUrl(
    content('hero.background_image'),
    'https://images.unsplash.com/photo-1767749995450-7b63ab7cd4fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxibGFjayUyMGx1eHVyeSUyMFNVViUyMGZsZWV0JTIwcGFya2VkJTIwVklQJTIwY2FyJTIwc2VydmljZXxlbnwwfHx8fDE3NzQ4NDU0MzZ8MA&ixlib=rb-4.1.0&q=85'
  );

  const testimonials = [
    {
      text: "My experience has been twice as good as I could ever imagine. Not only was I pleased but I just blush listening to my father tell everyone how professional, clean, and magnificent his experience was when he was picked up from the airport. He said my only complaint was the ride was too short.",
      name: "Courtney"
    },
    {
      text: "Ace was very professional when he picked me up from the airport. I come to Atlanta 2 or 3 times a month and dude really looks out. His Escalade is always clean and he knows the city well. Check him out.",
      name: "Samuel"
    },
    {
      text: "I had a date with my wife and I wasn't able to pick her up because of my work schedule. I used ACE and they picked her up in a very nice vehicle and were very professional, friendly, and most importantly a safe driver! Highly recommended.",
      name: "Leonardo"
    },
    {
      text: "ACE provided a true concierge driven experience for me! I had a quite extensive surgery and needed transportation to and from the medical center. ACE was professional, timely, and went above and beyond in being receptive and accommodating to my needs. Would highly recommend to anyone needing a personalized transportation experience!",
      name: "Jennifer"
    }
  ];

  const membershipTiers = [
    {
      name: "Lobby",
      suit: '\u2663', suitColor: 'text-gray-900',
      description: "Access. Priority. Community. The entry point to the ACE ecosystem. Priority scheduling, partner network, and member rate pricing.",
      tag: "Entry Level",
      discount: '8% off all rides',
    },
    {
      name: "Condo",
      suit: '\u2666', suitColor: 'text-red-600',
      description: "Advisory access, vehicle sourcing, and event support — built for the active ACE client. Monthly advisory session included.",
      tag: "Mid Tier",
      popular: true,
      discount: '12% off all rides',
    },
    {
      name: "Penthouse",
      suit: '\u2660', suitColor: 'text-gray-900',
      description: "Unlimited access. Full execution. Your Auto Concierge manages the outcome on your behalf. The highest tier of the ACE experience.",
      tag: "Top Tier",
      discount: '18% off all rides',
    }
  ];

  const fourPillars = [
    { suit: '\u2660', suitColor: 'text-white', icon: Shield, title: 'Preparation', description: 'Ready before the client arrives — every time, without exception.' },
    { suit: '\u2666', suitColor: 'text-red-500', icon: Thermometer, title: 'Environment', description: 'Temperature, scent, sound — the cabin environment is controlled before you enter.' },
    { suit: '\u2663', suitColor: 'text-white', icon: Target, title: 'Intentional Service', description: 'Nothing left to chance. Every detail considered before the moment arises.' },
    { suit: '\u2665', suitColor: 'text-red-500', icon: Feather, title: 'Friction Removal', description: 'Anticipate every need. Eliminate every ask. Deliver relief.' },
  ];

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribed(true);
    setEmail('');
    toast({ title: "Welcome to ACE", description: "You're in. Welcome to the ACE community." });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-home">
      {/* Hero Section */}
      <div className="relative h-screen overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt="Luxury Vehicle" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/50" />
        </div>
        
        <div className="relative h-full max-w-7xl mx-auto px-6 flex items-center">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight leading-tight" data-testid="hero-title">
              {content('hero.title', 'Atlanta Luxury Car Service Featuring Black SUVs, Exotic Sedans & Concierge Drivers')}
            </h1>
            <p className="text-base sm:text-lg text-gray-200 mb-4 max-w-2xl">
              {content('hero.subtitle', 'Private car service with black trucks and SUVs including Escalades, Suburbans, Tahoes, Yukon XLs, Sprinters, Maybach and Rolls Royce for VIP travel, airport transfers and special events.')}
            </p>
            <p className="text-sm text-gray-400 mb-8">
              Servicing Metro Atlanta: ATL Hartsfield-Jackson Airport &ndash; Midtown & Downtown &ndash; Buckhead &ndash; Marietta &ndash; Sandy Springs &ndash; Roswell &ndash; Alpharetta & More
            </p>
            <div className="flex gap-4 flex-wrap">
              <Button 
                onClick={() => navigate('/consultation')}
                className="h-14 px-8 bg-red-600 hover:bg-red-700 text-white text-lg font-semibold"
                data-testid="hero-consultation-btn"
              >
                Get a Quote
              </Button>
              <Button 
                onClick={() => navigate('/driven')}
                className="h-14 px-8 bg-white text-black hover:bg-gray-100 text-lg font-semibold"
                data-testid="hero-learn-more-btn"
              >
                Learn More
              </Button>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/50 rounded-full mt-2"></div>
          </div>
        </div>
      </div>

      {/* Auto Lifestyle GPS — front door (P4) */}
      <div className="bg-white border-y border-gray-200" data-testid="gps-front-door">
        <div className="max-w-7xl mx-auto px-6 py-12 sm:py-16 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-red-700 mb-3">♠ Auto Lifestyle GPS</p>
            <h2 className="text-lg md:text-lg font-black text-black leading-tight mb-3">WHAT'S YOUR AUTO LIFESTYLE?</h2>
            <p className="text-sm text-gray-600 mb-1">Where are you now? Where do you want to go?</p>
            <p className="text-sm text-gray-600">Are you Driven or Driving? ACE helps map the route.</p>
          </div>
          <div className="flex flex-col sm:flex-row md:flex-col lg:flex-row gap-3 md:justify-end">
            <Button onClick={() => navigate('/auto-lifestyle')} data-testid="home-map-my-route"
              className="bg-black text-white hover:bg-red-700 rounded-none px-7 py-6 text-xs uppercase tracking-[0.2em]">
              Map My Route <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <Button onClick={() => navigate('/booking')} variant="outline" data-testid="home-i-know-what-i-need"
              className="border-black text-black hover:border-red-700 hover:text-red-700 rounded-none px-7 py-6 text-xs uppercase tracking-[0.2em]">
              I Know What I Need
            </Button>
            <Button onClick={() => navigate('/contact')} variant="outline" data-testid="home-ask-concierge"
              className="border-gray-300 text-gray-600 hover:border-black hover:text-black rounded-none px-7 py-6 text-xs uppercase tracking-[0.2em]">
              Ask Your Auto Concierge
            </Button>
          </div>
        </div>
      </div>

      {/* Driven or Driving Service Slider — ACE Card Theme */}
      <div className="bg-black" data-testid="service-slider">
        <div className="max-w-7xl mx-auto px-6 py-12 sm:py-16">
          <div className="text-center mb-8">
            <p className="text-xs text-red-500 font-semibold tracking-widest mb-2">PICK YOUR CARD</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-white">How Can We Serve You?</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {/* Driven Card — Spade */}
            <button onClick={() => navigate('/driven')} className="relative group overflow-hidden rounded-2xl text-left bg-gray-950 border border-gray-800 hover:border-gray-600 transition-all" data-testid="slider-driven">
              <div className="absolute top-4 right-4 opacity-10 text-8xl font-serif text-white select-none z-0">{'\u2660'}</div>
              <div className="aspect-[16/9] overflow-hidden">
                <img src="https://images.unsplash.com/photo-1767749995458-b0927324e4d0?w=800&h=450&fit=crop" alt="Driven" className="w-full h-full object-cover opacity-50 group-hover:opacity-70 group-hover:scale-105 transition-all duration-500" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-transparent flex flex-col justify-end p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-3xl font-serif text-white">{'\u2660'}</span>
                  <span className="text-xs text-red-400 font-bold tracking-widest">CONCIERGE</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">Driven</h3>
                <p className="text-sm text-gray-300 mb-4 max-w-xs">Professional chauffeur. We drive you in luxury SUVs, sedans, and exotics.</p>
                <span className="inline-flex items-center text-sm font-semibold text-red-400 group-hover:text-red-300">
                  Explore Fleet <ChevronRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </button>

            {/* Driving Card — Diamond */}
            <button onClick={() => navigate('/driving')} className="relative group overflow-hidden rounded-2xl text-left bg-gray-950 border border-gray-800 hover:border-gray-600 transition-all" data-testid="slider-driving">
              <div className="absolute top-4 right-4 opacity-10 text-8xl font-serif text-red-500 select-none z-0">{'\u2666'}</div>
              <div className="aspect-[16/9] overflow-hidden">
                <img src="https://images.unsplash.com/photo-1764774518725-3ea2fc2460af?w=800&h=450&fit=crop" alt="Driving" className="w-full h-full object-cover opacity-50 group-hover:opacity-70 group-hover:scale-105 transition-all duration-500" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-transparent flex flex-col justify-end p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-3xl font-serif text-red-500">{'\u2666'}</span>
                  <span className="text-xs text-red-400 font-bold tracking-widest">LIFESTYLE</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">Driving</h3>
                <p className="text-sm text-gray-300 mb-4 max-w-xs">Auto lifestyle consulting. Vehicle sourcing, fleet management, and concierge advisory.</p>
                <span className="inline-flex items-center text-sm font-semibold text-red-400 group-hover:text-red-300">
                  Learn More <ChevronRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Fleet Gallery Preview — under Driven/Driving */}
      <div className="bg-black border-t border-gray-800 py-12 sm:py-16" data-testid="fleet-preview-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8">
            <p className="text-xs text-red-500 font-semibold tracking-widest mb-2">OUR FLEET</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Browse the Fleet</h2>
            <p className="text-sm text-gray-400">Concierge sedans & SUVs, Sprinter vans & buses, exotic vehicles</p>
          </div>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Button
              onClick={() => navigate('/rates')}
              size="lg"
              className="h-14 px-10 bg-red-600 hover:bg-red-700 text-white text-lg"
              data-testid="visit-fleet-gallery-btn"
            >
              View Rates <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              onClick={() => navigate('/fleet-gallery')}
              size="lg"
              variant="outline"
              className="h-14 px-8 border-white/30 text-white hover:bg-white/10 text-lg"
              data-testid="home-gallery-btn"
            >
              Fleet Gallery
            </Button>
            <Button
              onClick={() => navigate('/fleet/virtual-tour')}
              size="lg"
              variant="outline"
              className="h-14 px-8 border-white/30 text-white hover:bg-white/10 text-lg"
              data-testid="home-virtual-tour-btn"
            >
              Take a Tour
            </Button>
          </div>
        </div>
      </div>

      {/* Quick Quote Widget — under fleet preview */}
      <QuickQuote />

      {/* Featured Fleet Video */}
      {featuredVideo && (
        <div className="bg-black border-t border-gray-800 py-12 sm:py-16" data-testid="featured-video-section">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-6">
              <p className="text-xs text-red-500 font-semibold tracking-widest mb-2">THE ACE EXPERIENCE</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-white">See the Fleet in Action</h2>
            </div>
            <div className="rounded-xl overflow-hidden shadow-2xl ring-1 ring-white/10">
              <video src={`${API}${featuredVideo}`} className="w-full aspect-video" controls autoPlay muted loop playsInline preload="metadata" data-testid="homepage-featured-video" />
            </div>
          </div>
        </div>
      )}

      {/* Four Pillars of Luxury Service */}
      <div className="bg-black text-white py-24" data-testid="four-pillars-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-sm text-red-500 font-semibold tracking-widest mb-3">THE ACE STANDARD</p>
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">Four Pillars of <em className="text-red-500 not-italic">Luxury Service</em></h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {fourPillars.map((pillar, idx) => {
              return (
                <div key={idx} className="text-center group" data-testid={`pillar-${idx}`}>
                  <div className="h-16 w-16 mx-auto rounded-full border-2 border-red-600 flex items-center justify-center mb-5 group-hover:bg-red-600 transition-colors duration-300">
                    <span className={`text-2xl font-serif ${pillar.suitColor} group-hover:text-white transition-colors duration-300`}>{pillar.suit}</span>
                  </div>
                  <h3 className="text-xl font-bold mb-3">{pillar.title}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">{pillar.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Driven or Driving Podcast */}
      <div className="bg-gray-50 py-16 sm:py-20" data-testid="podcast-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-xs text-red-600 font-semibold tracking-widest mb-2">THE PODCAST</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-black mb-3">
              <em className="text-red-600 not-italic">Driven</em> or Driving
            </h2>
            <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
              Auto lifestyle conversations with JD Lawrence. Culture, cars, and the road to success.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Podcast Player Card */}
            <Card className="bg-black text-white border-0 overflow-hidden" data-testid="podcast-player">
              <div className="relative h-56 sm:h-64">
                <img src="https://images.unsplash.com/photo-1629019878688-f5d58a41b188?w=600&h=400&fit=crop" alt="Driven or Driving Podcast"
                  className="w-full h-full object-cover opacity-40" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="text-xs text-red-400 font-bold tracking-widest mb-2">LATEST EPISODE</p>
                  <h3 className="text-xl font-bold mb-1">Driven or Driving Podcast</h3>
                  <p className="text-sm text-gray-300">with JD Lawrence</p>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-sm text-gray-400 leading-relaxed">
                  Explore the intersection of automotive culture, entrepreneurship, and lifestyle. Every episode features real conversations about building a luxury brand from the ground up.
                </p>
                <div className="flex flex-wrap gap-2">
                  <a href="https://podcasts.apple.com" target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold bg-gray-800 hover:bg-gray-700 text-white px-4 py-2.5 rounded-lg transition-colors">
                    Apple Podcasts
                  </a>
                  <a href="https://open.spotify.com" target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold bg-green-900/50 hover:bg-green-800/50 text-green-400 px-4 py-2.5 rounded-lg transition-colors">
                    Spotify
                  </a>
                  <a href="https://youtube.com" target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold bg-red-900/50 hover:bg-red-800/50 text-red-400 px-4 py-2.5 rounded-lg transition-colors">
                    YouTube
                  </a>
                </div>
              </div>
            </Card>

            {/* Episode Highlights */}
            <div className="space-y-4" data-testid="podcast-episodes">
              <h3 className="text-lg font-bold text-black mb-2">Recent Episodes</h3>
              {[
                { title: 'Building ACE From the Ground Up', desc: 'How a vision for auto lifestyle consulting became Atlanta\'s premier concierge service.', tag: 'Entrepreneurship' },
                { title: 'The Art of the Auto Lifestyle', desc: 'What it means to be Driven — and why your vehicle says everything about your brand.', tag: 'Culture' },
                { title: 'Fleet Management for Entrepreneurs', desc: 'Monetizing your vehicles. Turning assets into income streams.', tag: 'Business' },
                { title: 'Exotic Car Market Breakdown', desc: 'Buying, maintaining, and profiting from exotic vehicles in today\'s market.', tag: 'Industry' },
              ].map((ep, i) => (
                <div key={i} className="flex gap-4 p-4 bg-white rounded-xl border border-gray-200 hover:shadow-md transition-shadow cursor-pointer" data-testid={`podcast-ep-${i}`}>
                  <div className="h-12 w-12 rounded-lg bg-black flex items-center justify-center shrink-0">
                    <Play className="h-5 w-5 text-red-500" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-[9px] font-bold text-red-600 uppercase tracking-wider">{ep.tag}</span>
                    </div>
                    <h4 className="text-sm font-bold text-black truncate">{ep.title}</h4>
                    <p className="text-xs text-gray-500 line-clamp-2">{ep.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ACE Memberships Preview */}
      <div className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-sm text-red-500 font-semibold tracking-widest mb-3">MEMBERSHIP</p>
            <h2 className="text-4xl sm:text-5xl font-bold text-black mb-4" data-testid="memberships-heading">Three Tiers. <em className="text-red-600 not-italic">One Standard.</em></h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">ACE membership provides ongoing access to your concierge's time, network, and execution capabilities — structured to match your lifestyle level.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {membershipTiers.map((tier, idx) => (
              <Card key={idx} className={`relative p-8 text-center hover:shadow-xl transition-all overflow-hidden ${tier.popular ? 'border-2 border-red-600 shadow-lg' : 'border-gray-200'}`} data-testid={`membership-tier-${idx}`}>
                <div className="absolute top-4 right-4 opacity-[0.06] text-8xl font-serif select-none">{tier.suit}</div>
                {tier.popular && (
                  <div className="text-xs font-bold text-red-600 tracking-wider mb-4">MOST POPULAR</div>
                )}
                <span className={`text-4xl font-serif block mb-2 ${tier.suitColor}`}>{tier.suit}</span>
                <p className="text-xs text-gray-500 font-semibold tracking-wider mb-2">{tier.tag}</p>
                <h3 className="text-2xl font-bold text-black mb-2">{tier.name}</h3>
                <p className="text-sm font-bold text-red-600 mb-4">{tier.discount}</p>
                <p className="text-gray-600 mb-6 text-sm leading-relaxed">{tier.description}</p>
                <Button 
                  onClick={() => navigate('/membership')}
                  variant="outline"
                  className={`${tier.popular ? 'border-red-600 text-red-600 hover:bg-red-600 hover:text-white' : 'border-gray-300 hover:bg-black hover:text-white'}`}
                >
                  EXPLORE {tier.name.toUpperCase()}
                </Button>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* JD Lawrence Quote Block */}
      <div className="bg-gray-50 py-20" data-testid="jd-quote-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Quote className="h-10 w-10 text-red-600 mx-auto mb-6 opacity-40" />
          <blockquote className="text-xl sm:text-2xl text-gray-800 leading-relaxed italic mb-8">
            "Luxury is not defined by the vehicle. Luxury is defined by <em className="text-red-600 not-italic font-semibold">preparation</em>, intentional service, control of the environment, and the removal of friction for the client."
          </blockquote>
          <div className="flex items-center justify-center gap-3">
            <div className="h-px w-8 bg-red-600" />
            <p className="text-sm font-bold text-black tracking-wider">JD LAWRENCE</p>
            <div className="h-px w-8 bg-red-600" />
          </div>
          <p className="text-xs text-gray-500 mt-1">Founder, Auto Concierge Enterprises</p>
        </div>
      </div>

      {/* Testimonials */}
      <div className="max-w-7xl mx-auto px-6 py-24">
        <h2 className="text-4xl font-bold text-black text-center mb-16" data-testid="testimonials-heading">
          {content('testimonials.heading', 'What Our Clients Are Saying')}
        </h2>
        <div className="grid md:grid-cols-2 gap-8">
          {testimonials.map((t, idx) => (
            <Card key={idx} className="p-8 border-gray-200" data-testid={`testimonial-${idx}`}>
              <p className="text-gray-600 italic mb-4 leading-relaxed">"{t.text}"</p>
              <p className="font-bold text-black">- {t.name}</p>
            </Card>
          ))}
        </div>
      </div>

      {/* Newsletter Signup */}
      <div className="bg-gray-50 py-20" data-testid="newsletter-section">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <p className="text-sm text-red-500 font-semibold tracking-widest mb-3">STAY IN THE LOOP</p>
          <h2 className="text-3xl font-bold text-black mb-3">The ACE Auto Lifestyle <em className="text-red-600 not-italic">Newsletter.</em></h2>
          <p className="text-gray-600 mb-8">Market intelligence, auto lifestyle insights, and ACE updates — delivered to your inbox. No fluff. No frequency overkill.</p>
          
          {subscribed ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6" data-testid="newsletter-success">
              <p className="font-bold text-green-800">You're in. Welcome to the ACE community.</p>
              <p className="text-sm text-green-600 mt-1">No spam. Unsubscribe anytime. ACE respects your inbox.</p>
            </div>
          ) : (
            <form onSubmit={handleNewsletterSubmit} className="flex gap-3 max-w-md mx-auto" data-testid="newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-red-600 text-sm"
                data-testid="newsletter-email-input"
              />
              <Button type="submit" className="bg-red-600 hover:bg-red-700 text-white px-6" data-testid="newsletter-submit-btn">
                <Mail className="h-4 w-4 mr-2" /> Subscribe
              </Button>
            </form>
          )}
        </div>
      </div>

      {/* CTA */}
      <div className="bg-black text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-4">
            {content('cta.title', 'Ready to elevate your auto lifestyle?')}
          </h2>
          <p className="text-base sm:text-lg text-gray-300 mb-8">
            {content('cta.description', 'Book your vehicle and experience directly — or start with a free Discovery Call.')}
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button 
              onClick={() => navigate('/booking')}
              size="lg"
              className="h-14 px-8 bg-red-600 hover:bg-red-700 text-white text-lg"
              data-testid="cta-book-btn"
            >
              Book Now
            </Button>
            <Button 
              onClick={() => navigate('/contact')}
              size="lg"
              variant="outline"
              className="h-14 px-8 border-white text-white hover:bg-white hover:text-black text-lg"
              data-testid="cta-contact-btn"
            >
              Discovery Call
            </Button>
          </div>
        </div>
      </div>
      <CMSCustomSections pageId="home" dark />
    </div>
  );
};

export default LandingHome;
